import { motion } from "framer-motion";
import { Icon } from "./Icon";

/**
 * Why the viewer is being stopped.
 *
 * - `missing` — no token at all; the reader probably has the wrong link.
 * - `denied` — a token was supplied and the server would not honor it.
 * - `expired` — the link was real and has run out. Its recovery is renewal
 *   rather than "check your link", so it gets its own copy.
 */
type AccessGateReason = "missing" | "denied" | "expired";

interface NarrativeAccessGateProps {
  /** Whether a token was provided but was invalid (vs. no token at all) */
  tokenInvalid?: boolean;
  /**
   * Overrides `tokenInvalid` when set. `tokenInvalid` stays for the bundled
   * `?token=` gate, which only has the two states.
   */
  reason?: AccessGateReason;
}

const GATE_COPY: Record<
  AccessGateReason,
  { headline: string; subtext: string; help: string }
> = {
  missing: {
    headline: "Access Required",
    subtext:
      "This report is private. You'll need a valid access link to view it.",
    help: "Please check your link or contact your account manager for access.",
  },
  denied: {
    headline: "Access Denied",
    subtext: "The access token in your link is not valid for this report.",
    help: "Please check your link or contact your account manager for access.",
  },
  expired: {
    headline: "Link Expired",
    subtext: "This report link has expired and is no longer available.",
    help: "Contact your account manager for a fresh link to this report.",
  },
};

/**
 * Shown when a narrative requires an access token that is missing, invalid,
 * or expired. Matches the visual style of NarrativeNotFound.
 */
export function NarrativeAccessGate({
  tokenInvalid = false,
  reason,
}: NarrativeAccessGateProps) {
  const copy = GATE_COPY[reason ?? (tokenInvalid ? "denied" : "missing")];

  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 text-white">
      {/* Subtle animated background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
                             radial-gradient(circle at 75% 75%, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: "50px 50px",
          }}
        />
      </div>

      {/* Content */}
      <motion.div
        className="relative z-10 flex max-w-md flex-col items-center text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        {/* Icon */}
        <motion.div
          className="mb-6 text-3xl flex h-20 w-20 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5, ease: "easeOut" }}
        >
          <Icon icon="fass lock" className="h-9 w-9 text-white/70" />
        </motion.div>

        {/* Headline */}
        <motion.h1
          className="mb-3 font-serif text-3xl font-semibold tracking-tight"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          {copy.headline}
        </motion.h1>

        {/* Subtext */}
        <motion.p
          className="mb-6 text-lg font-light leading-relaxed text-white/70"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          {copy.subtext}
        </motion.p>

        {/* Divider */}
        <motion.div
          className="mb-6 h-px w-24 bg-gradient-to-r from-transparent via-white/30 to-transparent"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        />

        {/* Help text */}
        <motion.p
          className="text-sm text-white/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          {copy.help}
        </motion.p>
      </motion.div>

      {/* Decorative gradient orbs */}
      <div className="pointer-events-none absolute -left-32 -top-32 h-64 w-64 rounded-full bg-purple-500/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" />
    </div>
  );
}
